import Big from 'big.js';

import type {
  Measure,
  Price,
  Quantity,
  RawQuantity,
} from '@personly/libs-types';

export type PartialQuantity = { measure?: Measure; value?: Big };

/**
 * Compare two measure objects, returning true if both
 * the asset and unit handles are equal
 *
 * @param {Measure} lhs
 * @param {Measure} rhs
 * @return True if both measure properties are equal to eachother
 */
const measuresEqual = (lhs: Measure, rhs: Measure) =>
  lhs.unitHandle === rhs.unitHandle && lhs.assetHandle === rhs.assetHandle;

/**
 * Compare two quantity objects, returning true if both
 * the measures and values are equal
 *
 * @param {Quantity} lhs
 * @param {Quantity} rhs
 * @return True if both quantities are equal to eachother
 */
const quantitiesEqual = (lhs: Quantity, rhs: Quantity) =>
  measuresEqual(lhs.measure, rhs.measure) && Big(lhs.value).eq(rhs.value);

const quantitiesPartiallyEqual = (
  lhs?: PartialQuantity,
  rhs?: PartialQuantity
) => {
  if (!lhs || !rhs) throw new Error('Nothing to compare!');

  if (!(lhs.value && rhs.value) && !(lhs.measure && rhs.measure))
    throw new Error('Nothing to compare!');

  if (lhs.value && rhs.value && !Big(lhs.value).eq(rhs.value)) return false;
  if (lhs.measure && rhs.measure && !measuresEqual(lhs.measure, rhs.measure))
    return false;
  return true;
};

const sortQuantities = (
  lhs: { measure: Measure },
  rhs: { measure: Measure }
) => {
  if (lhs.measure.assetHandle > rhs.measure.assetHandle) return -1;
  if (lhs.measure.assetHandle < rhs.measure.assetHandle) return 1;
  if (lhs.measure.unitHandle > rhs.measure.unitHandle) return 1;
  if (lhs.measure.unitHandle < rhs.measure.unitHandle) return -1;
  return 0;
};

/**
 * Takes a quantity with a possible Big value and convert value to POD.
 * Used before sending a quantity across the network boundary, since
 * the Big will not be preserved.
 *
 * @param {Quantity} quantity The quantity containing non POD value to flatten
 * @return A quantity containing value as POD
 */
const flattenQuantity = (quantity: Quantity) => ({
  ...quantity,
  value: Number.parseFloat(quantity.value.toString()),
});

const expandQuantity = (quantity: RawQuantity): Quantity => ({
  ...quantity,
  value: Big(quantity.value),
});

const complementQuantity = (quantity: Quantity) => ({
  ...quantity,
  value: Big(quantity.value).times(-1),
});

const factorizeQuantity = (quantity: Quantity, factor: number) => ({
  ...quantity,
  value: Big(quantity.value).times(factor),
});

const sumQuantities = (quantities: Quantity[]) =>
  quantities.reduce(
    (acc, quantity) => ({ ...quantity, value: acc.value.plus(quantity.value) }),
    { value: Big(0), measure: quantities[0].measure }
  );

const arrayifyQuantity = ({ measure, value }: Quantity) => [
  measure.assetHandle,
  measure.unitHandle,
  value.toString(),
];

/**
 * Compare two price objects, returning true if both
 * the measures, bases, and quotes are equal
 *
 * @param {Price} lhs
 * @param {Price} rhs
 * @return True if both prices are equal to eachother
 */
const pricesEqual = (lhs: Price, rhs: Price) =>
  measuresEqual(lhs.base, rhs.base) &&
  measuresEqual(lhs.quote, rhs.quote) &&
  Big(lhs.value).eq(rhs.value);

export {
  arrayifyQuantity,
  complementQuantity,
  expandQuantity,
  factorizeQuantity,
  flattenQuantity,
  measuresEqual,
  pricesEqual,
  quantitiesEqual,
  quantitiesPartiallyEqual,
  sortQuantities,
  sumQuantities,
};
